/**
 * formatUserDate — render a date the way THIS user asked to see dates.
 *
 * The profile pages were printing raw server values ("2011-06-05 00:00:00")
 * and ISO-ish `YYYY-MM-DD`, neither of which respects the Date Format the user
 * picked in their settings. The format token lives on
 * `window.serverData.userSetting.angularDateFormat` — the same source
 * `AppDatePicker` reads, so a date typed into a picker and a date printed in a
 * table now agree.
 *
 * The tokens are date-fns tokens (Angular's happen to match for every format
 * the product offers: `MM/dd/yyyy`, `d MMMM yyyy`, `dd.MM.yyyy`, `yyyy-MM-dd`…).
 * An unparseable value returns an empty string rather than "Invalid Date".
 */
import { format as formatDate } from 'date-fns';

const DEFAULT_FORMAT = 'd MMMM yyyy';

/** The user's chosen date format token, with the app-wide default as fallback. */
export function userDateFormat() {
	const fmt =
		typeof window !== 'undefined' &&
		window.serverData &&
		window.serverData.userSetting &&
		window.serverData.userSetting.angularDateFormat;
	return typeof fmt === 'string' && fmt ? fmt : DEFAULT_FORMAT;
}

/**
 * @param {string|number|Date|null} raw Anything `new Date()` accepts, including
 *   the "YYYY-MM-DD HH:mm:ss" the legacy tables carry.
 * @param {string} [fallback=''] Returned when the value cannot be read as a date.
 */
export function formatUserDate(raw, fallback = '') {
	if (raw == null || raw === '') return fallback;
	// "2026-10-01" and "2011-06-05 00:00:00" are calendar values, so they are
	// built as LOCAL dates: `new Date('2026-10-01')` is UTC midnight, which west
	// of Greenwich prints as the day before. The pattern is anchored so full JS
	// date strings and ISO values with a zone still go through `new Date`.
	const m =
		typeof raw === 'string' &&
		/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(raw.trim());
	const d =
		raw instanceof Date
			? raw
			: m
				? new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
				: new Date(raw);
	if (Number.isNaN(d.getTime())) return fallback;
	try {
		return formatDate(d, userDateFormat());
	} catch (e) {
		return formatDate(d, DEFAULT_FORMAT);
	}
}

export default formatUserDate;
